import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/JWTAuthContext';
import REQUEST from '../Services/Request';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
}));

const ReservationsListings = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    unitId: '',
    clientId: '',
    salesId: '',
    status: '',
    brokerName: '',
    company: '',
    brokerCompanyName: '',
    directManager: '',
    developerSales: '',
    developerCompany: '',
    developerTeamLeader: '',
    brokerTeamLeader: '',
    startDate: '',
    endDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchReservations();
  }, [filters, pagination.pageIndex, pagination.pageSize]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        PageIndex: pagination.pageIndex.toString(),
        PageSize: pagination.pageSize.toString(),
        ...(filters.unitId && { UnitId: filters.unitId }),
        ...(filters.clientId && { ClientId: filters.clientId }),
        ...(filters.salesId && { SalesId: filters.salesId }),
        ...(filters.status !== '' && { Status: filters.status }),
        ...(filters.brokerName && { BrokerName: filters.brokerName }),
        ...(filters.company && { Company: filters.company }),
        ...(filters.brokerCompanyName && { BrokerCompanyName: filters.brokerCompanyName }),
        ...(filters.directManager && { DirectManager: filters.directManager }),
        ...(filters.developerSales && { DeveloperSales: filters.developerSales }),
        ...(filters.developerCompany && { DeveloperCompany: filters.developerCompany }),
        ...(filters.developerTeamLeader && { DeveloperTeamLeader: filters.developerTeamLeader }),
        ...(filters.brokerTeamLeader && { BrokerTeamLeader: filters.brokerTeamLeader }),
        ...(filters.startDate && { StartDate: filters.startDate }),
        ...(filters.endDate && { EndDate: filters.endDate }),
      });

      const response = await REQUEST({
        method: "GET",
        url: `reservation/filter?${queryParams.toString()}`,
      });

      setReservations(response.Data || []);
      setPagination(prev => ({
        ...prev,
        totalCount: response.TotalCount || 0,
        totalPages: response.TotalPages || 0,
      }));
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load reservations',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPage + 1,
    }));
  };

  const handlePageSizeChange = (event) => {
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 1,
    }));
  };

  const handleSearch = () => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      unitId: '',
      clientId: '',
      salesId: '',
      status: '',
      brokerName: '',
      company: '',
      brokerCompanyName: '',
      directManager: '',
      developerSales: '',
      developerCompany: '',
      developerTeamLeader: '',
      brokerTeamLeader: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
  };

  const handleViewReservation = (reservation) => {
    setSelectedReservation(reservation);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReservation(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status) => {
    const statusMap = {
      0: { label: 'Pending', color: 'warning' },
      1: { label: 'Confirmed', color: 'success' },
      2: { label: 'Cancelled', color: 'error' },
      3: { label: 'Expired', color: 'default' },
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', color: 'default' };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const getTransactionTypeChip = (transactionType) => {
    const typeMap = {
      0: { label: 'Cash', color: 'primary' },
      1: { label: 'Installment', color: 'secondary' },
      2: { label: 'Mortgage', color: 'info' },
    };
    const typeInfo = typeMap[transactionType] || { label: 'Unknown', color: 'default' };
    return <Chip label={typeInfo.label} color={typeInfo.color} size="small" />;
  };

  return (
    <Container maxWidth="xl">
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Reservations Listings
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchReservations}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => console.log('Add new reservation')}
            >
              New Reservation
            </Button> */}
          </Box>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Search & Filters</Typography>
              <Button
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                sx={{ ml: 'auto' }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>
            
            {showFilters && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Unit ID"
                      name="unitId"
                      value={filters.unitId}
                      onChange={handleFilterChange}
                      placeholder="Search by unit ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Client ID"
                      name="clientId"
                      value={filters.clientId}
                      onChange={handleFilterChange}
                      placeholder="Search by client ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Sales ID"
                      name="salesId"
                      value={filters.salesId}
                      onChange={handleFilterChange}
                      placeholder="Search by sales ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        label="Status"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="0">Pending</MenuItem>
                        <MenuItem value="1">Confirmed</MenuItem>
                        <MenuItem value="2">Cancelled</MenuItem>
                        <MenuItem value="3">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Broker Name"
                      name="brokerName"
                      value={filters.brokerName}
                      onChange={handleFilterChange}
                      placeholder="Search by broker name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={filters.company}
                      onChange={handleFilterChange}
                      placeholder="Search by company"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Broker Company"
                      name="brokerCompanyName"
                      value={filters.brokerCompanyName}
                      onChange={handleFilterChange}
                      placeholder="Search by broker company"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Direct Manager"
                      name="directManager"
                      value={filters.directManager}
                      onChange={handleFilterChange}
                      placeholder="Search by direct manager"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Developer Sales"
                      name="developerSales"
                      value={filters.developerSales}
                      onChange={handleFilterChange}
                      placeholder="Search by developer sales"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Developer Company"
                      name="developerCompany"
                      value={filters.developerCompany}
                      onChange={handleFilterChange}
                      placeholder="Search by developer company"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Developer Team Leader"
                      name="developerTeamLeader"
                      value={filters.developerTeamLeader}
                      onChange={handleFilterChange}
                      placeholder="Search by developer team leader"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Broker Team Leader"
                      name="brokerTeamLeader"
                      value={filters.brokerTeamLeader}
                      onChange={handleFilterChange}
                      placeholder="Search by broker team leader"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="startDate"
                      type="date"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="End Date"
                      name="endDate"
                      type="date"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Data Grid Table */}
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Unit ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Client ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Transaction Type</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Broker Info</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Reservation Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Expiry Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map((reservation) => (
                        <tr key={reservation.Id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}>{reservation.Id}</td>
                          <td style={{ padding: '12px' }}>{reservation.UnitId}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ fontSize: '0.875rem' }}>
                              {reservation.ClientId}
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>{getStatusChip(reservation.Status)}</td>
                          <td style={{ padding: '12px' }}>{getTransactionTypeChip(reservation.TransactionType)}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ fontWeight: 'bold' }}>{reservation.BrokerName}</div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {reservation.BrokerCompanyName}
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            {new Date(reservation.ReservationDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {new Date(reservation.ExpiryDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewReservation(reservation)}
                              title="View Details"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>

                {reservations.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No reservations found
                    </Typography>
                  </Box>
                )}

                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={pagination.totalCount}
                  page={pagination.pageIndex - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </StyledPaper>

      {/* Reservation Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Reservation Details</DialogTitle>
        <DialogContent>
          {selectedReservation && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                <Typography variant="body1">{selectedReservation.Id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Unit ID</Typography>
                <Typography variant="body1">{selectedReservation.UnitId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Client ID</Typography>
                <Typography variant="body1">{selectedReservation.ClientId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Sales ID</Typography>
                <Typography variant="body1">{selectedReservation.SalesId || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {getStatusChip(selectedReservation.Status)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Transaction Type</Typography>
                {getTransactionTypeChip(selectedReservation.TransactionType)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Reservation Date</Typography>
                <Typography variant="body1">
                  {new Date(selectedReservation.ReservationDate).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Expiry Date</Typography>
                <Typography variant="body1">
                  {new Date(selectedReservation.ExpiryDate).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Reservation Fee</Typography>
                <Typography variant="body1">
                  {selectedReservation.ReservationFee ? `$${selectedReservation.ReservationFee}` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Installment Years</Typography>
                <Typography variant="body1">{selectedReservation.InstallmentYears || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">First Payment Date</Typography>
                <Typography variant="body1">
                  {selectedReservation.FirstPaymentDate ? new Date(selectedReservation.FirstPaymentDate).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Cancellation Reason</Typography>
                <Typography variant="body1">
                  {selectedReservation.CancellationReason || 'N/A'}
                </Typography>
              </Grid>
              
              {/* Broker Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Broker Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Broker Name</Typography>
                <Typography variant="body1">{selectedReservation.BrokerName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                <Typography variant="body1">{selectedReservation.Company}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Broker Company</Typography>
                <Typography variant="body1">{selectedReservation.BrokerCompanyName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Direct Manager</Typography>
                <Typography variant="body1">{selectedReservation.DirectManager}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Broker Team Leader</Typography>
                <Typography variant="body1">{selectedReservation.BrokerTeamLeader}</Typography>
              </Grid>
              
              {/* Developer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Developer Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Developer Sales</Typography>
                <Typography variant="body1">{selectedReservation.DeveloperSales}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Developer Company</Typography>
                <Typography variant="body1">{selectedReservation.DeveloperCompany}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Developer Team Leader</Typography>
                <Typography variant="body1">{selectedReservation.DeveloperTeamLeader}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Feedback</Typography>
                <Typography variant="body1">
                  {selectedReservation.Feedback || 'No feedback provided'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                <Typography variant="body1">
                  {selectedReservation.Notes || 'No notes provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">
                  {new Date(selectedReservation.CreatedAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                <Typography variant="body1">
                  {new Date(selectedReservation.UpdatedAt).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReservationsListings; 